import {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useMemo,
    lazy,
} from 'react'
import ErrorPage from '../components/UniversalComponents/ErrorPage'
import Home from '../components/Home/index'

// Code-split the heavy page components out of the initial bundle. These are
// rendered through <Outlet />, which is wrapped in a <Suspense> boundary in
// components/Navigation/index.tsx.
const Boats = lazy(() => import('../components/Admin/Boats'))
const Users = lazy(() => import('../components/Admin/Users'))
const Engines = lazy(() => import('../components/Admin/Engines'))
const Devices = lazy(() => import('../components/Admin/Devices'))
const Dealers = lazy(() => import('../components/Admin/Dealers/dealerIndex'))
const Signals = lazy(() => import('../components/Admin/Signals'))
const Subscriptions = lazy(() => import('../components/Admin/Subscriptions'))
const Manufacturer = lazy(() => import('../components/Admin/Manufacturers'))
const Dashboard = lazy(() => import('../components/Analytics/Dashboard'))
const PatchNotes = lazy(() => import('../components/Developer/PatchNotes'))
const SystemMonitor = lazy(
    () => import('../components/Developer/SystemMonitor')
)
const Support = lazy(() => import('../components/Support'))

import {
    createBrowserRouter,
    createRoutesFromElements,
    Route,
    RouterProvider,
    Navigate,
} from 'react-router-dom'
import NavSidebarRoot from '../components/Navigation'
import {
    Hub as HubIcon,
    Group as GroupIcon,
    DeveloperBoard,
    AutoGraph as AutoGraphIcon,
    SupportAgent,
} from '@mui/icons-material'

import { useAuth } from './AuthenticationContext'
import DeactivateAccount from '../components/LoginPage/DeactivateAccount'
import VerifyEmail from '../components/LoginPage/VerifyEmail'
import { useGlobalState } from './GlobalStateContext'

const defaultNavMenuLinks = [
    // {
    //     path: '/',
    //     element: <Home />,
    //     name: 'Fleet',
    //     errorElement: <ErrorPage />,
    //     icon: <HubIcon />,
    // },
    {
        path: '/',
        element: <Home />,
        name: 'Analytics',
        errorElement: <ErrorPage />,
        icon: <AutoGraphIcon />,
    },

    {
        path: 'users',
        name: 'Admin',
        errorElement: <ErrorPage />,
        icon: <GroupIcon />,
        superAdmin: false,
        children: [
            {
                path: 'admin/boats',
                element: <Boats />,
                name: 'Boats',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/dealers',
                element: <Dealers />,
                name: 'Dealers',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/devices',
                element: <Devices />,
                name: 'Devices',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/engines',
                element: <Engines />,
                name: 'Engines',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/manufacturer',
                element: <Manufacturer restoredData={''} />,
                name: 'Manufacturer',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/signals',
                element: <Signals />,
                name: 'Signals',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/subscriptions',
                element: <Subscriptions />,
                name: 'Subscriptions',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/users',
                element: <Users />,
                name: 'Users',
                errorElement: <ErrorPage />,
            },
            // {
            //     path: 'admin/boatregistration',
            //     element: <BoatRegistrationForm />,
            //     name: 'Boat Registration Form',
            //     errorElement: <ErrorPage />,
            // },
        ],
    },

    // {
    //     path: 'analytics',
    //     element: <Dashboard />,
    //     name: 'Analytics',
    //     errorElement: <ErrorPage />,
    //     superAdmin: false,
    //     icon: <AutoGraphIcon />,
    // },
    {
        path: 'System',
        element: <Home />,
        name: 'System',
        errorElement: <ErrorPage />,
        icon: <DeveloperBoard />,
        superAdmin: true,
        children: [
            {
                path: 'system/patch-notes',
                element: <PatchNotes />,
                name: 'Patch Notes',
                errorElement: <ErrorPage />,
            },
            {
                path: 'system/system-monitoring',
                element: <SystemMonitor />,
                name: 'System Monitoring',
                errorElement: <ErrorPage />,
            },
        ],
    },
    {
        path: 'Support',
        element: <Support />,
        name: 'Support',
        errorElement: <ErrorPage />,
        icon: <SupportAgent />,
        superAdmin: true,
    },
]

export const NavMenu = createContext({ navMenuLinks: defaultNavMenuLinks })

export const NavMenuContext = () => {
    const defaultState = {
        navMenuLinks: defaultNavMenuLinks,
    }
    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const { navMenuLinks } = state

    const currentUser = useAuth()
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const auth = currentUser.auth
    const { authenticated } = auth
    const { userProfile } = oupUser || {}
    // Guests / users without a profile have no userProfile; guard so the whole
    // app doesn't crash to a blank screen while destructuring.
    const { superAdmin, userProfilePermissions } = userProfile || {}

    // Checking if the user is a Super Admin
    const isSuperAdmin = superAdmin === true

    // checking userProfilePermissions from Auth
    const permissions = useMemo(
        () =>
            Object.keys(userProfilePermissions || {}).filter(
                (permission) => userProfilePermissions[permission].read
            ),
        [userProfilePermissions]
    )

    /// Filter nav on admin
    const adminPermissions = navMenuLinks[1].children?.filter((child) =>
        permissions.includes(child.name)
    )
    const clonedAdmin = { ...navMenuLinks[1], children: adminPermissions }

    ///Filter nave on data hub
    const dataHubPermissions = navMenuLinks[2].children?.filter((child) =>
        permissions.includes(child.name)
    )
    const clonedDataHub = { ...navMenuLinks[2], children: dataHubPermissions }
    const newNavMenu: any = isSuperAdmin
        ? defaultNavMenuLinks
        : [navMenuLinks[0], clonedAdmin, clonedDataHub]

    // Build the router only when the menu or permissions change. Previously this
    // ran on every render — createBrowserRouter is expensive and recreating it
    // discards in-memory router state. The two static routes are also added once
    // here instead of once per top-level link (which produced duplicate keys).
    const router = useMemo(() => {
        const links: JSX.Element[] = []
        navMenuLinks?.forEach(
            ({ path, element, name, children, superAdmin }) => {
                if (children && children.length !== 0) {
                    children.forEach((child) =>
                        links.push(
                            <Route
                                path={child.path}
                                key={child.name}
                                element={
                                    (isSuperAdmin && superAdmin) ||
                                    permissions.includes(child.name) ||
                                    isSuperAdmin ? (
                                        child.element
                                    ) : (
                                        <Navigate to="/error" />
                                    )
                                }
                            />
                        )
                    )
                } else {
                    links.push(
                        <Route path={path} key={name} element={element} />
                    )
                }
            }
        )
        links.push(
            <Route
                path="deactivate-account"
                key="deactivate-account-key"
                element={<DeactivateAccount />}
            />
        )
        links.push(
            <Route
                path="/verify-user/:id"
                key="verify-user-email-by-id"
                element={<VerifyEmail />}
            />
        )
        return createBrowserRouter(
            createRoutesFromElements(
                <Route
                    path="/"
                    errorElement={<ErrorPage />}
                    element={<NavSidebarRoot />}
                >
                    {links}
                </Route>
            )
        )
    }, [navMenuLinks, isSuperAdmin, permissions])

    useEffect(() => {
        if (authenticated) {
            setState({ navMenuLinks: newNavMenu })
        }
    }, [authenticated])

    return (
        <NavMenu.Provider value={{ navMenuLinks: newNavMenu }}>
            <RouterProvider router={router} />
        </NavMenu.Provider>
    )
}

export const useNavMenu = () => useContext(NavMenu)
